import Sentry from '../../config/sentry'

import TurnstileWidget from './TurnstileWidget.class'
import TurnstileModal from './TurnstileModal.class'

import { TURNSTILE_ERROR_CODES } from './constants'

const ERROR_CODE = {
  USER_CANCEL_TURNSTILE: 113918,
  TURNSTILE_ERROR: 113919,
}

const ERROR_NAME = {
  113918: 'CANCEL_BUTTON_CLICKED',
  113919: 'TURNSTILE_FAILED',
}

export default class TurnstileManager {
  static SCHEME = 'insign://security-check/turnstile'
  static CALLBACK_NAME = 'turnstileCallback'
  static ERROR_CALLBACK_NAME = 'turnstileErrorCallback'
  static errorRetryCount = 0

  constructor() {
    window[TurnstileManager.CALLBACK_NAME] = TurnstileManager.turnstileCallback
    window[TurnstileManager.ERROR_CALLBACK_NAME] = TurnstileManager.turnstileErrorCallback

    this.widgetInstance = new TurnstileWidget({
      callbackName: TurnstileManager.CALLBACK_NAME,
      errorCallbackName: TurnstileManager.ERROR_CALLBACK_NAME,
    })
  }

  showTurnstile({
    message,
  }) {
    const widgetEl = this.widgetInstance.createWidgetEl()
    const modal = new TurnstileModal({
      content: widgetEl,
      onClose: TurnstileManager.sendCloseSignalToMobile,
    })

    modal.show({ message })

    TurnstileManager.sendInitializedSignalToMobile()
  }

  static turnstileCallback(turnstileToken) {
    TurnstileManager.sendSuccessSignalToMobile(turnstileToken)
  }

  /**
   * Turnstile 에러 코드를 기반으로 에러가 재시도 가능한지 여부를 반환합니다.
   * 재시도 가능한 에러일 경우, 최대 3회까지 재시도를 진행합니다.
   *
   * * @param {string} turnstileErrorCode Turnstile 에러 코드 (6자리 숫자로 구성된 문자열)
   */
  static isRetryableError(turnstileErrorCode) {
    if (typeof turnstileErrorCode !== 'string') {
      console.error('Turnstile error code should be a string')
      return false
    }

    /**
     * 에러 코드 패턴을 정규식으로 변환합니다.
     * - '*' 문자는 숫자(\d)로 변환합니다.
     * - ex. '3001*' -> /^3001\d$/
     */
    const patternToRegex = (pattern) => new RegExp(`^${pattern.replace(/\*/g, '\\d')}$`)
    const isPatternMatched = (pattern) => patternToRegex(pattern).test(turnstileErrorCode)

    const retriableErrorCodes = Object.values(TURNSTILE_ERROR_CODES).filter(({ retry }) => retry)

    const hasMatchedRetryableErrorCode = retriableErrorCodes.some(({ codes }) => {
      const isMatched = codes.some(isPatternMatched)
      return isMatched
    })

    return hasMatchedRetryableErrorCode
  }

  /**
   * Tunrstile 에러 발생 시 에러 코드가 문자열로 전달됩니다.
   * - ref. https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
   *
   * @param {string} turnstileErrorCode Turnstile 에러 코드 (6자리 숫자로 구성된 문자열)
   */
  static turnstileErrorCallback(turnstileErrorCode) {
    const isRetryableError = TurnstileManager.isRetryableError(turnstileErrorCode)

    if (isRetryableError && TurnstileManager.errorRetryCount < 3) {
      Sentry.addBreadcrumb({
        type: 'info',
        category: 'turnstile',
        message: `Retryable error occurred in Turnstile with error code: ${turnstileErrorCode}`,
        level: 'info',
      })
      TurnstileManager.errorRetryCount += 1
      return
    }

    TurnstileManager.errorRetryCount = 0
    TurnstileManager.sendErrorSignalToMobile(turnstileErrorCode)
  }

  static sendInitializedSignalToMobile() {
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'turnstile',
      message: 'Turnstile initialized',
      level: 'info',
    })

    const responseData = {
      success: true,
      initialized: true,
    }

    TurnstileManager.sendSignalToMobile(responseData)
  }

  static sendSuccessSignalToMobile(turnstileToken) {
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'turnstile',
      message: 'Turnstile succeeded',
      level: 'info',
    })

    const responseData = {
      success: true,
      turnstile_token: turnstileToken,
    }

    TurnstileManager.sendSignalToMobile(responseData)
  }

  static sendCloseSignalToMobile() {
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'turnstile',
      message: 'Turnstile cancelled by user',
      level: 'info',
    })

    const responseData = {
      success: false,
      error_code: ERROR_CODE.USER_CANCEL_TURNSTILE,
      error_name: ERROR_NAME[ERROR_CODE.USER_CANCEL_TURNSTILE],
    }

    TurnstileManager.sendSignalToMobile(responseData)
  }

  static sendErrorSignalToMobile(turnstileErrorCode) {
    Sentry.withScope((scope) => {
      const error = new Error(`An error occurred in Turnstile with error code: ${turnstileErrorCode}`)
      error.name = `[${ERROR_CODE.TURNSTILE_ERROR}] ${ERROR_NAME[ERROR_CODE.TURNSTILE_ERROR]}`

      scope.setTag('turnstile_error_code', turnstileErrorCode)

      Sentry.captureException(error)
    })

    const responseData = {
      success: false,
      error_code: ERROR_CODE.TURNSTILE_ERROR,
      error_name: ERROR_NAME[ERROR_CODE.TURNSTILE_ERROR],
      turnstile_error_code: turnstileErrorCode,
    }

    TurnstileManager.sendSignalToMobile(responseData)
  }

  static sendSignalToMobile(params) {
    const queryParams = new URLSearchParams(params)
    const redirectUrl = `${TurnstileManager.SCHEME}?${queryParams.toString()}`

    window.location.href = redirectUrl
  }
}
